// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {
	getDefaultMessageGroupSpacing,
	getMessageGroupSpacingPatch,
} from '@app/features/accessibility/state/MessageGroupSpacing';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import MessageStylePreference, {
	type MessageStyle,
	MessageStyles,
} from '@app/features/theme/state/MessageStylePreference';
import type {ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {canResetSliderValue, SliderResetIconButton} from '@app/features/ui/components/slider/SliderResetIconButton';
import {ArrowsDownUpIcon, ListIcon, RowsIcon} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {ChoiceCard, choiceCardStyles} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ChoiceCard';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/MessagesTab.module.css';
import {
	FONT_SIZE_OPTIONS,
	FontSizeTabContent,
	getNearestFontSize,
} from '@app/features/user/components/modals/tabs/appearance_tab/ScalingTab';
import {CompactComboboxRow} from '@app/features/user/components/modals/tabs/components/CompactComboboxRow';
import UserSettings from '@app/features/user/state/UserSettings';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo} from 'react';

const COMFORTABLE_DESCRIPTOR = msg({
	message: 'Comfortable',
	comment: 'Density option in Appearance: roomy message rows. Keep it concise.',
});
const COMPACT_DESCRIPTOR = msg({
	message: 'Compact',
	comment: 'Density option in Appearance: tight message rows. Keep it concise.',
});
const DENSITY_DESCRIPTOR = msg({
	message: 'Density',
	comment: 'Heading of the Comfortable / Compact choice in Appearance. Keep it concise.',
});
const DENSITY_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Space between rows and messages.',
	comment: 'One-line description under the Density heading in Appearance.',
});
const MESSAGE_STYLE_DESCRIPTOR = msg({
	message: 'Message style',
	comment: 'Accessible name of the Bubbles / Classic cards in Appearance.',
});
const BUBBLES_DESCRIPTOR = msg({
	message: 'Bubbles',
	comment: 'Message style card name: messages in bubbles, yours on the right. Keep it concise.',
});
const BUBBLES_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Yours on the right',
	comment: 'Short description under the Bubbles message style card.',
});
const CLASSIC_DESCRIPTOR = msg({
	message: 'Classic',
	comment: 'Message style card name: flat rows with everyone on the left. Keep it concise.',
});
const CLASSIC_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Everyone on the left',
	comment: 'Short description under the Classic message style card.',
});
const TEXT_SIZE_DESCRIPTOR = msg({
	message: 'Text size',
	comment: 'Heading above the chat font size row in Appearance. Keep it concise.',
});
const HIDE_USER_AVATARS_DESCRIPTOR = msg({
	message: 'Hide user avatars',
	comment: 'Short label in the messages tab. Keep it concise.',
});
const SPACE_BETWEEN_MESSAGE_GROUPS_DESCRIPTOR = msg({
	message: 'Space between message groups',
	comment: 'Label in the messages tab.',
});
const PIXELS_DESCRIPTOR = msg({
	message: '{messageGroupSpacing}px',
	comment: 'Message group spacing option label.',
});
const RESET_SPACING_DESCRIPTOR = msg({
	message: 'Reset spacing',
	comment: 'Button below the space between message groups setting. Restores message group spacing to the default.',
});
const STYLE_DESCRIPTOR = msg({
	message: 'Style',
	comment: 'Phone Appearance row: Bubbles or Classic message style. Keep it concise.',
});
const COMPACT_DENSITY_DESCRIPTOR = msg({
	message: 'Compact density',
	comment: 'Phone Appearance switch: tight message rows instead of comfortable ones. Keep it concise.',
});
const COMPACT_DENSITY_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Tighter bubbles and rows',
	comment: 'Short line under the Compact density switch on the phone.',
});
const REDUCE_MOTION_DESCRIPTOR = msg({
	message: 'Reduce motion',
	comment: 'Phone Appearance switch: turns animations and transitions off. Keep it concise.',
});
const MESSAGE_GROUP_SPACING_OPTIONS = [0, 4, 8, 16, 24] as const;
type Density = 'comfortable' | 'compact';

const BubblesArt: React.FC = () => (
	<span className={styles.styleArt} aria-hidden="true">
		<span className={styles.bubble} style={{width: '60%'}} />
		<span className={clsx(styles.bubble, styles.bubbleMine)} style={{width: '45%'}} />
		<span className={styles.bubble} style={{width: '52%'}} />
	</span>
);

const ClassicArt: React.FC = () => (
	<span className={styles.styleArt} aria-hidden="true">
		{[30, 70, 26, 58].map((width, index) => (
			<span key={index} className={styles.classicLine}>
				<span className={clsx(styles.classicAvatar, index % 2 === 1 && styles.classicAvatarHidden)} />
				<span className={styles.classicText} style={{width: `${width}%`}} />
			</span>
		))}
	</span>
);

/** KONVO round 6: Bubbles / Classic as preview cards, kept per device. */
const MessageStyleCards: React.FC = observer(() => {
	const {i18n} = useLingui();
	const options: ReadonlyArray<{value: MessageStyle; name: string; description: string; preview: React.ReactNode}> = [
		{
			value: MessageStyles.BUBBLES,
			name: i18n._(BUBBLES_DESCRIPTOR),
			description: i18n._(BUBBLES_DESCRIPTION_DESCRIPTOR),
			preview: <BubblesArt />,
		},
		{
			value: MessageStyles.CLASSIC,
			name: i18n._(CLASSIC_DESCRIPTOR),
			description: i18n._(CLASSIC_DESCRIPTION_DESCRIPTOR),
			preview: <ClassicArt />,
		},
	];
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
		event.preventDefault();
		const next = MessageStylePreference.style === MessageStyles.BUBBLES ? MessageStyles.CLASSIC : MessageStyles.BUBBLES;
		MessageStylePreference.setStyle(next);
		const group = event.currentTarget.parentElement;
		requestAnimationFrame(() => group?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus());
	};
	return (
		<div
			className={clsx(choiceCardStyles.grid, styles.styleCards)}
			role="radiogroup"
			aria-label={i18n._(MESSAGE_STYLE_DESCRIPTOR)}
			data-flx="user.appearance-tab.messages-tab.message-style"
		>
			{options.map((option) => (
				<ChoiceCard
					key={option.value}
					name={option.name}
					description={option.description}
					checked={MessageStylePreference.style === option.value}
					preview={option.preview}
					onSelect={() => MessageStylePreference.setStyle(option.value)}
					onKeyDown={handleKeyDown}
					data-flx={`user.appearance-tab.messages-tab.message-style.${option.value}`}
				/>
			))}
		</div>
	);
});

const getNearestMessageGroupSpacing = (value: number): number => {
	let nearest: number = MESSAGE_GROUP_SPACING_OPTIONS[0];
	let nearestDistance = Number.POSITIVE_INFINITY;
	for (const option of MESSAGE_GROUP_SPACING_OPTIONS) {
		const distance = Math.abs(option - value);
		if (distance < nearestDistance) {
			nearest = option;
			nearestDistance = distance;
		}
	}
	return nearest;
};

const resolveMessageGroupSpacingInput = (
	inputValue: string,
	options: ReadonlyArray<ComboboxOption<number>>,
): number | undefined => {
	const numericMatch = inputValue.trim().match(/([0-9]+(?:\.[0-9]+)?)/);
	if (!numericMatch) return undefined;
	const parsedValue = Number(numericMatch[1]);
	if (!Number.isFinite(parsedValue)) return undefined;
	return options.reduce((nearest, option) =>
		Math.abs(option.value - parsedValue) < Math.abs(nearest.value - parsedValue) ? option : nearest,
	).value;
};
/**
 * KONVO phone refresh (p1-settings): the Messages card on the phone. Style as the kit lens,
 * text size as a slider between a small and a large A, then density and reduced motion.
 */
const MobileMessagesContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const fontSize = getNearestFontSize(Accessibility.fontSize);
	const fontIndex = Math.max(0, FONT_SIZE_OPTIONS.indexOf(fontSize as (typeof FONT_SIZE_OPTIONS)[number]));
	const sizeAt = (index: number): number =>
		FONT_SIZE_OPTIONS[Math.min(FONT_SIZE_OPTIONS.length - 1, Math.max(0, Math.round(index)))];
	return (
		<>
			<SettingsRow
				title={i18n._(STYLE_DESCRIPTOR)}
				control={({labelId}) => (
					<Segmented<MessageStyle>
						value={MessageStylePreference.style}
						onChange={(value) => MessageStylePreference.setStyle(value)}
						aria-labelledby={labelId}
						options={[
							{value: MessageStyles.BUBBLES, children: i18n._(BUBBLES_DESCRIPTOR)},
							{value: MessageStyles.CLASSIC, children: i18n._(CLASSIC_DESCRIPTOR)},
						]}
						data-flx="user.appearance-tab.messages-tab.mobile.style"
					/>
				)}
				data-flx="user.appearance-tab.messages-tab.mobile.style-row"
			/>
			<SettingsRow
				id="chat-font-scaling"
				title={i18n._(TEXT_SIZE_DESCRIPTOR)}
				className={styles.phoneSliderRow}
				control={({labelId}) => (
					<span className={styles.phoneSlider}>
						<span className={styles.phoneSliderA} aria-hidden="true">
							A
						</span>
						<Slider
							value={fontIndex}
							defaultValue={fontIndex}
							factoryDefaultValue={FONT_SIZE_OPTIONS.indexOf(16)}
							minValue={0}
							maxValue={FONT_SIZE_OPTIONS.length - 1}
							step={1}
							ariaLabelledBy={labelId}
							ariaValueText={`${fontSize}px`}
							onValueRender={(index) => `${sizeAt(index)}px`}
							onValueChange={(index) => {
								const next = sizeAt(index);
								if (next !== Accessibility.fontSize) AccessibilityCommands.update({fontSize: next});
							}}
							data-flx="user.appearance-tab.messages-tab.mobile.text-size"
						/>
						<span className={clsx(styles.phoneSliderA, styles.phoneSliderALarge)} aria-hidden="true">
							A
						</span>
					</span>
				)}
				data-flx="user.appearance-tab.messages-tab.mobile.text-size-row"
			/>
			<Switch
				label={i18n._(COMPACT_DENSITY_DESCRIPTOR)}
				description={i18n._(COMPACT_DENSITY_DESCRIPTION_DESCRIPTOR)}
				value={UserSettings.messageDisplayCompact}
				onChange={(value) => UserSettings.setMessageDisplayCompact(value)}
				data-flx="user.appearance-tab.messages-tab.mobile.compact"
			/>
			<Switch
				label={i18n._(REDUCE_MOTION_DESCRIPTOR)}
				value={Accessibility.useReducedMotion}
				onChange={(value) =>
					AccessibilityCommands.update({syncReducedMotionWithSystem: false, reducedMotionOverride: value})
				}
				data-flx="user.appearance-tab.messages-tab.mobile.reduce-motion"
			/>
		</>
	);
});

export const MessagesTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const {messageDisplayCompact} = UserSettings;
	const messageGroupSpacing = Accessibility.getMessageGroupSpacingValue(messageDisplayCompact);
	const selectedMessageGroupSpacing = getNearestMessageGroupSpacing(messageGroupSpacing);
	const defaultMessageGroupSpacing = getDefaultMessageGroupSpacing(messageDisplayCompact);
	const canResetMessageGroupSpacing = canResetSliderValue(messageGroupSpacing, defaultMessageGroupSpacing);
	const showUserAvatarsInCompactMode = Accessibility.showUserAvatarsInCompactMode;
	const mobileLayout = MobileLayout;
	const messageGroupSpacingOptions: ReadonlyArray<ComboboxOption<number>> = useMemo(
		() =>
			MESSAGE_GROUP_SPACING_OPTIONS.map((value) => ({
				value,
				label: i18n._(PIXELS_DESCRIPTOR, {messageGroupSpacing: value}),
			})),
		[i18n.locale],
	);
	useEffect(() => {
		if (messageGroupSpacing === selectedMessageGroupSpacing) return;
		AccessibilityCommands.update(getMessageGroupSpacingPatch(messageDisplayCompact, selectedMessageGroupSpacing));
	}, [messageDisplayCompact, messageGroupSpacing, selectedMessageGroupSpacing]);
	const density: Density = messageDisplayCompact ? 'compact' : 'comfortable';
	if (mobileLayout.enabled) {
		return <MobileMessagesContent />;
	}
	return (
		<>
			<div className={styles.layout} data-flx="user.appearance-tab.messages-tab.messages-tab-content.layout">
				<MessageStyleCards />
				<div className={styles.block} data-flx="user.appearance-tab.messages-tab.messages-tab-content.density">
					<div className={styles.blockHeader}>
						<h4 className={styles.blockTitle}>{i18n._(DENSITY_DESCRIPTOR)}</h4>
						<p className={styles.blockDescription}>{i18n._(DENSITY_DESCRIPTION_DESCRIPTOR)}</p>
					</div>
					<Segmented<Density>
						value={density}
						onChange={(value) => UserSettings.setMessageDisplayCompact(value === 'compact')}
						aria-label={i18n._(DENSITY_DESCRIPTOR)}
						className={styles.densitySegmented}
						options={[
							{value: 'comfortable', children: i18n._(COMFORTABLE_DESCRIPTOR), icon: <ListIcon size={16} />},
							{value: 'compact', children: i18n._(COMPACT_DESCRIPTOR), icon: <RowsIcon size={16} />},
						]}
						data-flx="user.appearance-tab.messages-tab.messages-tab-content.density-segmented"
					/>
				</div>
			</div>
			<div className={styles.block} data-flx="user.appearance-tab.messages-tab.messages-tab-content.text-size">
				<div className={styles.blockHeader}>
					<h4 className={styles.blockTitle}>{i18n._(TEXT_SIZE_DESCRIPTOR)}</h4>
				</div>
				<div className={styles.rows}>
					<FontSizeTabContent
						id="chat-font-scaling"
						data-flx="user.appearance-tab.messages-tab.messages-tab-content.font-size-tab-content"
					/>
					<CompactComboboxRow<number>
						icon={<ArrowsDownUpIcon size={20} />}
						label={i18n._(SPACE_BETWEEN_MESSAGE_GROUPS_DESCRIPTOR)}
						action={
							canResetMessageGroupSpacing ? (
								<SliderResetIconButton
									canReset={true}
									onReset={() =>
										AccessibilityCommands.update(
											getMessageGroupSpacingPatch(messageDisplayCompact, defaultMessageGroupSpacing),
										)
									}
									ariaLabel={i18n._(RESET_SPACING_DESCRIPTOR)}
									dataFlx="user.appearance-tab.messages-tab.messages-tab-content.reset-button.spacing"
									data-flx="user.appearance-tab.messages-tab.messages-tab-content.slider-reset-icon-button"
								/>
							) : null
						}
						value={selectedMessageGroupSpacing}
						options={messageGroupSpacingOptions}
						onChange={(value) =>
							AccessibilityCommands.update(getMessageGroupSpacingPatch(messageDisplayCompact, value))
						}
						autoSelectValueFromInput={resolveMessageGroupSpacingInput}
						controlWidth="small"
						menuMinWidth={128}
						aria-label={i18n._(SPACE_BETWEEN_MESSAGE_GROUPS_DESCRIPTOR)}
						dataFlx="user.appearance-tab.messages-tab.messages-tab-content.select.spacing"
						data-flx="user.appearance-tab.messages-tab.messages-tab-content.compact-select-row.update"
					/>
					{messageDisplayCompact && !MessageStylePreference.bubbles ? (
						<Switch
							label={i18n._(HIDE_USER_AVATARS_DESCRIPTOR)}
							value={!showUserAvatarsInCompactMode}
							onChange={(value) => AccessibilityCommands.update({showUserAvatarsInCompactMode: !value})}
							data-flx="user.appearance-tab.messages-tab.messages-tab-content.switch.update"
						/>
					) : null}
				</div>
			</div>
		</>
	);
});
